import { checkIsAfterOrEqualDate } from 'aa_common/front-end'
import { DEPRECIATION_METHOD_CODE } from 'common/constants'
import { loadNs } from 'common/i18n-config'
import {
  MAX_WARNING_UNIT_ACQUISITION_COST,
  MIN_WARNING_UNIT_ACQUISITION_COST,
  START_USAGE_THRESHOLD_DATE,
} from 'containers/assets/common/const'
import { array, number, object } from 'yup'

import { DEPRECIATION_FIELDS, FORM_FIELDS } from '../const'

const t = loadNs(['components/organisms/assets/asset-form', 'common'])

// The 300,000 ceiling only applies once the asset is in service on/before the rule date.
// While the usage-started date is blank we can only check preventively, so the 400,000
// ceiling applies whatever the acquired/transition date is; the 300,000-400,000 band is a
// warning owned by the confirmation dialog in use-confirm-check-warnings.ts.
function getSmallAmountAssetUnitCostLimit(usageStartedAt?: string) {
  return !usageStartedAt || checkIsAfterOrEqualDate(usageStartedAt, START_USAGE_THRESHOLD_DATE)
    ? MAX_WARNING_UNIT_ACQUISITION_COST
    : MIN_WARNING_UNIT_ACQUISITION_COST
}

function isSmallAmountAssetAcquisitionCostValid(limit: number, acquisitionCost?: number, quantity?: number) {
  // Can only validate when we know the acquisition cost.
  if (acquisitionCost === undefined || acquisitionCost === null) {
    return true
  }
  // A quantity of 0 (or blank) is treated as 1.
  const divisor = quantity && quantity > 0 ? quantity : 1

  return acquisitionCost / divisor < limit
}

function isBookValueAtChangeValidWithAcquisitionCost(value?: number, acquisitionCost?: number) {
  return value === undefined || acquisitionCost === undefined || value <= acquisitionCost
}

function isBookValueAtChangeValidWithServiceLifeAfterChange(
  value?: number,
  acquisitionCost?: number,
  serviceLife?: number,
  serviceLifeAfterChange?: number
) {
  return (
    value === undefined ||
    acquisitionCost === undefined ||
    serviceLife === undefined ||
    serviceLifeAfterChange === undefined ||
    value !== acquisitionCost ||
    serviceLife === serviceLifeAfterChange
  )
}

function isBookValueAtChangeValidWithMemorandumAmount(value?: number, memorandumValue?: number) {
  return value === undefined || memorandumValue === undefined || memorandumValue === null || value >= memorandumValue
}

function isServiceLifeAfterChangeValid(serviceLife?: number, serviceLifeAfterChange?: number) {
  return serviceLife === undefined || serviceLifeAfterChange === undefined || serviceLife >= serviceLifeAfterChange
}

let generalErrors: {
  [key: string]: string
} = {}

export const getDepreciationSchema = (isD80AcquisitionCostValidationEnabled: boolean) =>
  array()
    .of(
      object().shape({
        [DEPRECIATION_FIELDS.MEMORANDUM_VALUE]: number().nullable(),
        [DEPRECIATION_FIELDS.SERVICE_LIFE]: number(),
        [DEPRECIATION_FIELDS.SERVICE_LIFE_AFTER_CHANGE]: number().test(
          'service_life_after_change_over_service_life',
          t('service_life_after_change_over_service_life'),
          (value, context) => {
            const isValid = isServiceLifeAfterChangeValid(context.parent?.[DEPRECIATION_FIELDS.SERVICE_LIFE], value)
            generalErrors[`${context.path}_test1`] = isValid ? '' : t('service_life_after_change_general_error')
            return isValid
          }
        ),
        [DEPRECIATION_FIELDS.BOOK_VALUE_AT_CHANGE]: number()
          .test(
            'book_value_at_change_over_acquisition_cost',
            t('book_value_at_change_over_acquisition_cost'),
            (value, context) => {
              const parent = context.from?.find(parent => parent?.value[FORM_FIELDS.BOOK_VALUE_REFERENCE] !== undefined)
              const isValid = isBookValueAtChangeValidWithAcquisitionCost(
                value,
                parent?.value[FORM_FIELDS.BOOK_VALUE_REFERENCE]
              )
              generalErrors[`${context.path}_test1`] = isValid ? '' : t('book_value_at_change_general_error')

              return isValid
            }
          )
          .test(
            'booking_value_at_change_and_service_life_after_change',
            t('booking_value_at_change_and_service_life_after_change'),
            (value, context) => {
              const rootParent = context.from?.find(
                parent => parent?.value[FORM_FIELDS.BOOK_VALUE_REFERENCE] !== undefined
              )
              const isValid = isBookValueAtChangeValidWithServiceLifeAfterChange(
                value,
                rootParent?.value[FORM_FIELDS.BOOK_VALUE_REFERENCE],
                context.parent?.[DEPRECIATION_FIELDS.SERVICE_LIFE],
                context.parent?.[DEPRECIATION_FIELDS.SERVICE_LIFE_AFTER_CHANGE]
              )
              generalErrors[`${context.path}_test2`] = isValid ? '' : t('book_value_at_change_general_error')
              return isValid
            }
          )
          .test(
            'book_value_at_change_under_memorandum_amount',
            t('book_value_at_change_under_memorandum_amount'),
            (value, context) => {
              const isValid = isBookValueAtChangeValidWithMemorandumAmount(
                value,
                context.parent?.[DEPRECIATION_FIELDS.MEMORANDUM_VALUE]
              )
              generalErrors[`${context.path}_test3`] = isValid ? '' : t('book_value_at_change_general_error')
              return isValid
            }
          ),
        [DEPRECIATION_FIELDS.DEPRECIATION_METHOD_CODE]: number()
          .nullable()
          .test(
            'depreciation_method_code_is_valid',
            t('small_amount_depreciable_asset_acquisition_cost_limit'),
            (value, context) => {
              // Only validate small-amount depreciable assets (D80) when the feature is on.
              const isValidField = true
              if (
                !isD80AcquisitionCostValidationEnabled ||
                value !== DEPRECIATION_METHOD_CODE.SMALL_AMOUNT_DEPRECIABLE_ASSET
              ) {
                generalErrors[`${context.path}_test1`] = ''
                return isValidField
              }

              const rootParent = context.from?.find(
                parent => parent?.value[FORM_FIELDS.BOOK_VALUE_REFERENCE] !== undefined
              )
              const usageStartedAt = rootParent?.value?.[FORM_FIELDS.USAGE_STARTED_AT]
              const acquisitionCost =
                rootParent?.value?.[FORM_FIELDS.NET_ACQUISITION_COST] ??
                rootParent?.value?.[FORM_FIELDS.ACQUISITION_COST_REFERENCE]
              const quantity = rootParent?.value?.[FORM_FIELDS.QUANTITY]

              const limit = getSmallAmountAssetUnitCostLimit(usageStartedAt)
              const limitErrorKey =
                limit === MAX_WARNING_UNIT_ACQUISITION_COST
                  ? 'small_amount_depreciable_asset_acquisition_cost_limit'
                  : 'small_amount_depreciable_asset_acquisition_cost_limit_before_rule_effective_date'

              generalErrors[`${context.path}_test1`] = isSmallAmountAssetAcquisitionCostValid(
                limit,
                acquisitionCost,
                quantity
              )
                ? ''
                : t(limitErrorKey)

              return isValidField
            }
          ),
      })
    )
    .nullable()

function getLedgerErrorFromValidation(): string[] {
  const errors = new Set<string>()
  Object.keys(generalErrors).forEach(key => {
    if (generalErrors[key]) {
      errors.add(generalErrors[key])
    }
  })
  return Array.from(errors)
}

function getLedgerErrorFromErrors(errors: Record<string, any>): string[] {
  const errorSet = new Set<string>()

  Object.keys(errors).forEach(key => {
    key.includes(DEPRECIATION_FIELDS.BOOK_VALUE_AT_CHANGE) && errorSet.add(t('book_value_at_change_general_error'))
    key.includes(DEPRECIATION_FIELDS.SERVICE_LIFE_AFTER_CHANGE) &&
      errorSet.add(t('service_life_after_change_general_error'))
  })

  return Array.from(errorSet)
}

export function getLedgerGeneralValidationErrors(errors?: any) {
  return errors && typeof errors === 'object' && Object.keys(errors).length
    ? getLedgerErrorFromErrors(errors)
    : getLedgerErrorFromValidation()
}

export function resetLedgerGeneralValidationErrors() {
  generalErrors = {}
}
